import { useCallback, useEffect, useRef, useState } from 'react';
export type EventSourceHandler = (data: string, event: MessageEvent<string>) => void;
export interface StartEventSourceParams {
  url: string;
  withCredentials?: boolean;
}
export interface UseEventSourceOptions {
  onMessage: EventSourceHandler;
  onOpen?: (event: Event) => void;
  onError?: (error: Event) => void;
  closeOnEvents?: string[];
  eventHandlers?: Record<string, EventSourceHandler>;
}
export const useEventSource = (options: UseEventSourceOptions) => {
  const eventSourceRef = useRef<EventSource | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const optionsRef = useRef(options);
  // eslint-disable-next-line react-hooks/refs -- stale closure 방지를 위해 최신 options를 ref에 동기화하는 의도적 패턴
  optionsRef.current = options;
  const close = useCallback(() => {
    eventSourceRef.current?.close();
    eventSourceRef.current = null;
    setIsStreaming(false);
  }, []);
  const start = useCallback(({
    url,
    withCredentials
  }: StartEventSourceParams) => {
    close();
    const eventSource = new EventSource(url, {
      withCredentials
    });
    eventSourceRef.current = eventSource;
    setIsStreaming(true);
    eventSource.onopen = event => {
      optionsRef.current.onOpen?.(event);
    };
    eventSource.onmessage = event_0 => {
      optionsRef.current.onMessage(event_0.data, event_0);
    };
    for (const [eventName, handler] of Object.entries(optionsRef.current.eventHandlers ?? {})) {
      eventSource.addEventListener(eventName, event_1 => {
        const messageEvent = event_1 as MessageEvent<string>;
        handler(messageEvent.data, messageEvent);
        if (optionsRef.current.closeOnEvents?.includes(eventName)) {
          close();
        }
      });
    }
    eventSource.onerror = event_2 => {
      optionsRef.current.onError?.(event_2);
      close();
    };
  }, [close]);
  useEffect(() => close, [close]);
  return {
    start,
    close,
    isStreaming
  };
};